import { useEffect } from 'react';

const ATTRIBUTE = 'data-air-plus-sticky-column';
const LEFT = '--air-plus-sticky-left';
const BACKGROUND = '--air-plus-sticky-background';
const TABLES = '[data-air-plus-content] table, .ant-modal-root table, .ant-drawer table';
const EXCLUDED = '.ant-picker-panel, .ant-calendar, .fc, [data-air-plus-sticky="off"]';

// Use the rendered grid, so native tables, AntD's split header/body tables,
// selection columns and grouped headers all use the same measured offsets.
export function getStickyColumnCells(table, count = 3) {
  const widths = [];
  const cells = [];
  let occupied = [];
  let section;
  Array.from(table.rows).forEach((row) => {
    if (row.classList.contains('ant-table-measure-row')) return;
    if (row.parentElement !== section) {
      section = row.parentElement;
      occupied = [];
    }
    let column = 0;
    Array.from(row.cells).forEach((cell) => {
      while (occupied[column] > 0) column += 1;
      const span = cell.colSpan || 1;
      const width = cell.getBoundingClientRect().width;
      cells.push({ cell, column, span, width });
      if (span === 1 && width > 0) widths[column] = width;
      for (let i = column; i < column + span; i += 1) {
        occupied[i] = Math.max(occupied[i] || 0, cell.rowSpan || 1);
      }
      column += span;
    });
    occupied = occupied.map((remaining) => Math.max(0, remaining - 1));
  });
  // Colgroups provide exact column widths even for an empty AntD body.
  Array.from(table.children).filter((child) => child.tagName === 'COLGROUP').forEach((group) => {
    Array.from(group.children).forEach((col, index) => {
      const width = col.getBoundingClientRect().width;
      if (width > 0) widths[index] = width;
    });
  });
  return cells.filter(({ cell, column, span, width }) =>
    column + span <= count && width > 0 && getComputedStyle(cell).display === 'table-cell',
  ).map(({ cell, column, span }) => ({
    cell, column,
    left: Array.from({ length: column }, (_, index) => widths[index] || 0).reduce((sum, width) => sum + width, 0),
    last: column + span === count,
  }));
}

function opaqueBackground(cell) {
  for (let node = cell; node; node = node.parentElement) {
    const color = getComputedStyle(node).backgroundColor;
    if (color && color !== 'transparent' && (!color.startsWith('rgba') || /,\s*1\)$/.test(color))) return color;
  }
  return '#fff';
}

export function useStickyAirPlusColumns() {
  useEffect(() => {
    let frame = null;
    const marked = new Set();
    const observed = new Set();
    const clear = () => {
      marked.forEach((cell) => {
        cell.removeAttribute(ATTRIBUTE);
        cell.removeAttribute('data-air-plus-sticky-edge');
        cell.style.removeProperty(LEFT);
        cell.style.removeProperty(BACKGROUND);
      });
      marked.clear();
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(refresh);
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    function refresh() {
      frame = null;
      // Restore original styles before reading row colors, including selection
      // and striped backgrounds. Attribute writes are not observed below.
      clear();
      const tables = new Set(Array.from(document.querySelectorAll(TABLES)).filter((table) =>
        !table.closest(EXCLUDED) && getComputedStyle(table).display === 'table' && table.getBoundingClientRect().width > 0,
      ));
      observed.forEach((table) => {
        if (!tables.has(table)) { resize?.unobserve(table); observed.delete(table); }
      });
      const plans = [];
      tables.forEach((table) => {
        if (!observed.has(table)) { resize?.observe(table); observed.add(table); }
        const configuredCount = table.closest('[data-air-plus-sticky-columns]')?.getAttribute('data-air-plus-sticky-columns');
        const count = configuredCount === null || configuredCount === undefined ? 3 : Number(configuredCount);
        getStickyColumnCells(table, count).forEach((item) => plans.push({ ...item, background: opaqueBackground(item.cell) }));
      });
      plans.forEach(({ cell, column, left, last, background }) => {
        cell.style.setProperty(LEFT, `${left}px`);
        cell.style.setProperty(BACKGROUND, background);
        cell.setAttribute(ATTRIBUTE, String(column));
        if (last) cell.setAttribute('data-air-plus-sticky-edge', '');
        marked.add(cell);
      });
    }
    const mutations = new MutationObserver(schedule);
    mutations.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-air-plus-sticky-columns'] });
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      mutations.disconnect();
      resize?.disconnect();
      window.removeEventListener('resize', schedule);
      if (frame !== null) cancelAnimationFrame(frame);
      clear();
    };
  }, []);
}
